import * as React from 'react'
import { useEffect, useState } from 'react'
import { DocumentChecklistItem, DocumentChecklistResponse, DocumentRecord } from '@logic'
import {
  ChecklistPanel,
  ChecklistHeader,
  ChecklistProgress,
  ChecklistBody,
  ChecklistItemRow,
} from '@modules/documents/style'
import { FlexRow } from '@src/styles'
import { AppContext } from '@src/app'
import { Service } from '@src/service'

interface Props {
  organizationId: string
  documents: DocumentRecord[] | undefined
  canEdit: boolean
  defaultExpanded: boolean
  onLinkFromDrive?: (item: DocumentChecklistItem) => void
}

interface InternalProps extends Props {
  service: Service
}

const CHECKLIST_KEYWORDS: Record<string, string[]> = {
  'bylaws': ['bylaws', 'operating agreement'],
  'certificate-of-incorporation': ['certificate of incorporation', 'articles of organization', 'articles of incorporation'],
  'ein': ['ein', 'employer identification', 'tax id'],
  'board-consents': ['board consent', 'board resolution'],
  'term-sheet': ['term sheet'],
  'stock-certificates': ['stock certificate'],
  'convertible-note': ['convertible note', 'form of note', 'promissory note'],
  'subscription-agreements': ['subscription agreement', 'holder agreement'],
  'transfer-agreements': ['transfer agreement'],
  'equity-incentive-plans': ['incentive plan', 'stock option plan', 'equity plan', 'esop'],
  'cap-table': ['cap table', 'capitalization table'],
}

function autoMatchDocuments(
  documents: DocumentRecord[] | undefined,
  items: DocumentChecklistItem[]
): DocumentChecklistItem[] {
  if (!documents || documents.length === 0) return items
  const fileNames = documents
    .filter(d => d.type !== 2)
    .map(d => d.name.toLowerCase())

  return items.map(item => {
    const keywords = CHECKLIST_KEYWORDS[item.key] || []
    const matched = keywords.some(kw =>
      fileNames.some(fn => fn.includes(kw.toLowerCase()))
    )
    return { ...item, autoMatched: matched && !item.checked }
  })
}

function DocumentChecklistInner(props: InternalProps) {
  const { organizationId, documents, canEdit, defaultExpanded, service, onLinkFromDrive } = props
  const [items, setItems] = useState<DocumentChecklistItem[]>([])
  const [expanded, setExpanded] = useState(defaultExpanded)
  const [loading, setLoading] = useState(true)

  const fetchChecklist = async () => {
    try {
      const response: DocumentChecklistResponse = await (service as any).getDocumentChecklist(organizationId)
      if (response?.items) {
        const matched = autoMatchDocuments(documents, response.items)
        setItems(matched)
      }
    } catch (e) {
      console.error('Failed to load document checklist', e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchChecklist()
  }, [organizationId])

  useEffect(() => {
    if (items.length > 0 && documents) {
      setItems(autoMatchDocuments(documents, items))
    }
  }, [documents])

  const checkedCount = items.filter(i => i.checked).length
  const totalCount = items.length || 11
  const percentage = totalCount > 0 ? (checkedCount / totalCount) * 100 : 0

  const handleToggle = async (item: DocumentChecklistItem) => {
    if (!canEdit) return
    const newChecked = !item.checked
    setItems(prev =>
      prev.map(i => i.key === item.key ? { ...i, checked: newChecked, autoMatched: false } : i)
    )
    try {
      await (service as any).updateDocumentChecklistItem(organizationId, item.key, { checked: newChecked })
    } catch (e) {
      setItems(prev =>
        prev.map(i => i.key === item.key ? { ...i, checked: !newChecked } : i)
      )
      console.error('Failed to update checklist item', e)
    }
  }

  if (loading) return null

  return (
    <ChecklistPanel>
      <ChecklistHeader onClick={() => setExpanded(!expanded)}>
        <FlexRow alignItems="center">
          <span className="checklist-title">Document Upload Checklist</span>
          <span className="checklist-progress-text">{checkedCount} of {totalCount} completed</span>
        </FlexRow>
        <svg
          className={`checklist-chevron ${expanded ? 'expanded' : ''}`}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </ChecklistHeader>
      <ChecklistProgress percentage={percentage}>
        <div className="bar" />
      </ChecklistProgress>
      <ChecklistBody expanded={expanded}>
        {items.map(item => (
          <ChecklistItemRow key={item.key} suggested={item.autoMatched}>
            <input
              type="checkbox"
              checked={item.checked}
              disabled={!canEdit}
              onChange={() => handleToggle(item)}
            />
            <span className="item-label">{item.label}</span>
            {item.autoMatched && <span className="suggested-badge">suggested</span>}
            {canEdit && onLinkFromDrive && (
              <button
                className="drive-link-button"
                onClick={e => {
                  e.stopPropagation()
                  onLinkFromDrive(item)
                }}
              >
                Link from Drive
              </button>
            )}
          </ChecklistItemRow>
        ))}
      </ChecklistBody>
    </ChecklistPanel>
  )
}

export const DocumentChecklist = (props: Props) => (
  <AppContext.Consumer>
    {context => <DocumentChecklistInner {...props} service={context.service!} />}
  </AppContext.Consumer>
)
